import React, { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, DateDisplay, Input, Modal, useToast } from "@aidkitorg/component-library";
import { DateInput } from "../Components/DateInput";
import { usePost } from "../API";

const L = {
  uids_label: "Enter comma-separated applicant UIDs",
  date_filter_checkbox_label: "Would you like to delete only messages before a certain date?",
  date_filter_label: "Delete messages before",
  delete_button: "Delete",
  delete_messages_title: "Delete Messages",
  number_of_applicants_text: "This will delete messages for $n applicant$s.",
  date_filter_prefix: "This will delete their messages before",
  all_messages_prefix: "This will delete ",
  all_messages_all: "all",
  all_messages_suffix: "of their messages",
  reason_label: "Please provide a reason for this deletion.",
  cancel_button: "Cancel",
  applicants_to_review_title: "Applicants to review",
  applicants_to_review_description: "We were unable to delete all of the messages for these applicants because the message contact is used by multiple applicants.",
  toast: "Deleted messages!",
}

export const DeleteMessages: React.FC = () => {
  const deleteMessagesForApplicants = usePost("/messages/delete_messages_for_applicants");
  const { toast } = useToast();

  const [isShowingConfirmModal, setIsShowingConfirmModal] = useState(false);
  const [applicantUidsString, setApplicantUidsString] = useState("");
  const [includeDateFilter, setIncludeDateFilter] = useState(false);
  const [dateFilter, setDateFilter] = useState<Date | undefined>(undefined);
  const [appsToReviewForSharedMessages, setAppsToReviewForSharedMessages] = useState<string[] | undefined>();

  const handleChangeApplicantUids = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setApplicantUidsString(e.currentTarget.value);
  }, []);

  const handleToggleIncludeDateFilter = useCallback(() => {
    setIncludeDateFilter((includeDateFilter) => !includeDateFilter);
  }, []);

  const handleChangeDateFilter = useCallback((value: string) => {
    if (!value) {
      return setDateFilter(undefined);
    }

    const [monthString, dayString, yearString] = value.split("/");
    const monthIndex = +monthString - 1;
    const day = +dayString;
    const year = +yearString;
    setDateFilter(new Date(year, monthIndex, day));
  }, []);

  const handleClickDelete = useCallback(() => {
    setIsShowingConfirmModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async (
    applicantUids: string[],
    deleteMessagesBefore: string | undefined,
    reason: string,
  ) => {
    const response = await deleteMessagesForApplicants({
      applicantUids,
      deleteMessagesBefore,
      reason,
    });

    setIsShowingConfirmModal(false);
    setAppsToReviewForSharedMessages(response?.appsToReviewForSharedMessages);

    if (response && !("error" in response) && !response.appsToReviewForSharedMessages.length) {
      toast({ title: L.toast, variant: "success" });
    }
  }, [deleteMessagesForApplicants]);

  const handleCloseConfirmModal = useCallback(() => {
    setIsShowingConfirmModal(false);
  }, []);

  const handleCloseAppsToReviewModal = useCallback(() => {
    setAppsToReviewForSharedMessages(undefined);
  }, []);

  console.log(dateFilter, 'DF');

  return (
    <div>
      {isShowingConfirmModal &&
        <DeleteMessagesConfirmModal
          applicantUidsString={applicantUidsString}
          includeDateFilter={includeDateFilter}
          dateFilter={dateFilter}
          onClickConfirm={handleConfirmDelete}
          onClose={handleCloseConfirmModal}
        />
      }
      {!!appsToReviewForSharedMessages && appsToReviewForSharedMessages.length > 0 &&
        <AppsToReviewModal appsToReviewForSharedMessages={appsToReviewForSharedMessages} onClose={handleCloseAppsToReviewModal} />
      }
      <div className="my-3">
        <Input
          label={L.uids_label}
          value={applicantUidsString}
          onChange={handleChangeApplicantUids}
        />
      </div>
      <Checkbox
        label={L.date_filter_checkbox_label}
        checked={includeDateFilter}
        onChange={handleToggleIncludeDateFilter}
      />
      {includeDateFilter &&
        <div className="mt-3">
          <DateInput
            key="datefilter"
            label={L.date_filter_label}
            setValue={handleChangeDateFilter}
          />
        </div>
      }

      <Button
        size="md"
        variant="primary"
        className="mt-3"
        isDisabled={!applicantUidsString || (includeDateFilter && !dateFilter)}
        onClick={handleClickDelete}>
        {L.delete_button}
      </Button>
    </div>
  );
};

interface DeleteMessagesConfirmModalProps {
  applicantUidsString: string;
  includeDateFilter: boolean;
  dateFilter: Date | undefined;
  onClickConfirm: (applicantUids: string[], deleteMessagesBefore: string | undefined, reason: string) => void;
  onClose: () => void;
}

const DeleteMessagesConfirmModal: React.FC<DeleteMessagesConfirmModalProps> = ({
  applicantUidsString,
  includeDateFilter,
  dateFilter,
  onClickConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");

  const applicantUids = useMemo(() => {
    return applicantUidsString
      .split(",")
      .map((applicantUid) => applicantUid.trim())
      .filter((applicantUid) => !!applicantUid);
  }, [applicantUidsString]);

  const handleChangeReason = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.currentTarget.value);
  }, []);

  const handleClickDelete = useCallback(() => {
    const deleteMessagesBefore = includeDateFilter && dateFilter ? JSON.stringify(dateFilter) : undefined;
    onClickConfirm(applicantUids, deleteMessagesBefore, reason);
  }, [applicantUids, includeDateFilter, dateFilter, reason, onClickConfirm]);

  return (
    <Modal title={L.delete_messages_title} open onClose={onClose}>
      <p>{
        L.number_of_applicants_text
          .replace('$n', applicantUids.length.toString())
          .replace('$s', applicantUids.length !== 1 ? "s" : "")
      }</p>
      {includeDateFilter && dateFilter
        ? <p>{L.date_filter_prefix} <DateDisplay className="text-base text-unset" utcValue={+dateFilter} variant="date" displayInUTC={false} /></p>
        : <p>{L.all_messages_prefix} <b>{L.all_messages_all}</b> {L.all_messages_suffix}</p>
      }
      <Input
        label={L.reason_label}
        value={reason}
        onChange={handleChangeReason}
      />

      <div className="flex justify-end gap-x-3 mt-3">
        <Button size="md" variant="secondary" onClick={onClose}>{L.cancel_button}</Button>
        <Button size="md" variant="primary" isDisabled={!reason} onClick={handleClickDelete}>{L.delete_button}</Button>
      </div>
    </Modal>
  );
};

interface AppsToReviewModalProps {
  appsToReviewForSharedMessages: string[];
  onClose: () => void;
}

const AppsToReviewModal: React.FC<AppsToReviewModalProps> = ({
  appsToReviewForSharedMessages,
  onClose,
}) => {
  return (
    <Modal
      title={L.applicants_to_review_title}
      open
      onClose={onClose}
    >
      <p>{L.applicants_to_review_description}</p>
      <ul>
        {appsToReviewForSharedMessages.map((app) => (
          <li key={app}>
            <Link to={`/a/${app}`} target="_blank">{app}</Link>
          </li>
        ))}
      </ul>
    </Modal>
  );
};
